// Mon compte : identité, rôles, préférences de l'appareil (thème, vue Terrain/Expert), mot de passe.
import { useState, type FormEvent } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { KeyRound, LogOut, Phone, Settings, ShieldCheck } from 'lucide-react';

import { api, messageErreur } from '../../lib/api.ts';
import { useSession, type Moi } from '../../lib/session.tsx';
import { useTheme, useVue } from '../../lib/preferences.ts';
import { Alerte, Badge, Bouton, Champ, Dialogue, EntetePage, LienBouton, Panneau, useConfirmation, useNotifier } from '../../components/ui.tsx';
import { ActivationMfa } from './ActivationMfa.tsx';
import { DUREE_JOURS, retirer, useHorsLigne } from '../../lib/horsLigne.ts';
import { useDeconnexion } from '../../app/horsConnexion.tsx';
import { depuis } from '../../lib/format.ts';

export function FormulaireMotDePasse({ impose, apres }: { impose?: boolean; apres?: () => void }) {
  const [actuel, setActuel] = useState('');
  const [nouveau, setNouveau] = useState('');
  const [confirme, setConfirme] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const notifier = useNotifier();
  const qc = useQueryClient();
  const valider = async (e: FormEvent) => {
    e.preventDefault();
    setErreur(null);
    if (nouveau.length < 12) { setErreur('Le nouveau mot de passe doit comporter au moins 12 caractères.'); return; }
    if (nouveau !== confirme) { setErreur('Les deux saisies du nouveau mot de passe sont différentes.'); return; }
    setEnvoi(true);
    try {
      await api.post('/api/auth/mot-de-passe', { actuel, nouveau });
      setActuel(''); setNouveau(''); setConfirme('');
      notifier('Mot de passe modifié.');
      await qc.invalidateQueries({ queryKey: ['moi'] });
      apres?.();
    } catch (err) { setErreur(messageErreur(err)); } finally { setEnvoi(false); }
  };
  return (
    <form className="pile" onSubmit={valider} noValidate>
      {erreur && <Alerte ton="danger">{erreur}</Alerte>}
      <Champ libelle={impose ? 'Mot de passe provisoire' : 'Mot de passe actuel'} type="password" autoComplete="current-password" value={actuel} onChange={(e) => setActuel(e.target.value)} />
      <Champ libelle="Nouveau mot de passe" type="password" autoComplete="new-password" value={nouveau} onChange={(e) => setNouveau(e.target.value)}
        aide="12 caractères minimum. Une phrase de plusieurs mots est plus sûre et plus facile à retenir." />
      <Champ libelle="Confirmer le nouveau mot de passe" type="password" autoComplete="new-password" value={confirme} onChange={(e) => setConfirme(e.target.value)} />
      <div><Bouton type="submit" variante="principal" chargement={envoi} icone={<KeyRound aria-hidden />}>Enregistrer le mot de passe</Bouton></div>
    </form>
  );
}

/**
 * Sécurité du compte, en tête de « Mon compte » : changer de mot de passe et activer la double authentification s'ouvrent dans un
 * dialogue, dont le bouton d'enregistrement est visible sans défiler à toutes les tailles (les formulaires étaient en bas de page,
 * sous la ligne de flottaison).
 */
function PanneauSecurite({ mfa }: { mfa: boolean }) {
  const [ouvert, setOuvert] = useState<'mdp' | 'mfa' | null>(null);
  return (
    <Panneau titre="Sécurité du compte">
      <div className="pile">
        <div className="compte-ligne">
          <span className="compte-ligne-texte"><strong>Double authentification</strong>
            {mfa ? <span className="petit secondaire"> Un code de votre application d’authentification est demandé à chaque connexion. Téléphone perdu : demandez à l’administrateur de la réinitialiser.</span>
              : <span className="petit secondaire"> Un code à 6 chiffres de votre téléphone, en plus du mot de passe.</span>}</span>
          {mfa ? <Badge ton="succes">Activée</Badge>
            : <Bouton variante="principal" icone={<ShieldCheck aria-hidden />} onClick={() => setOuvert('mfa')}>Activer la double authentification</Bouton>}
        </div>
        <div className="compte-ligne">
          <span className="compte-ligne-texte"><strong>Mot de passe</strong><span className="petit secondaire"> 12 caractères minimum.</span></span>
          <Bouton icone={<KeyRound aria-hidden />} onClick={() => setOuvert('mdp')}>Changer de mot de passe</Bouton>
        </div>
      </div>
      {ouvert === 'mdp' && (
        <Dialogue titre="Changer de mot de passe" ouvert fermer={() => setOuvert(null)}>
          <FormulaireMotDePasse apres={() => setOuvert(null)} />
        </Dialogue>
      )}
      {ouvert === 'mfa' && (
        <Dialogue titre="Activer la double authentification" ouvert fermer={() => setOuvert(null)}>
          <ActivationMfa apres={() => setOuvert(null)} />
        </Dialogue>
      )}
    </Panneau>
  );
}

/** Écran imposé tant que le mot de passe provisoire n'a pas été remplacé. */
export function EcranMotDePasseImpose({ moi }: { moi: Moi }) {
  return (
    <div className="connexion-panneau" style={{ minHeight: '100dvh' }}>
      <div className="pile-l" style={{ width: '100%', maxWidth: 420 }}>
        <div className="pile" style={{ gap: 6 }}>
          <span className="marque-nom" style={{ fontSize: 'var(--t-xl)' }}>TRAME</span>
          <h1 style={{ fontSize: 'var(--t-xl)' }}>Choisissez votre mot de passe</h1>
          <p className="secondaire">Bonjour {moi.utilisateur.nom}. Votre compte a été créé avec un mot de passe provisoire : remplacez-le pour continuer.</p>
        </div>
        <FormulaireMotDePasse impose />
      </div>
    </div>
  );
}

/** Téléphone PROFESSIONNEL : seul numéro enregistré, montré aux collègues dans le carnet des sites où vous êtes intervenu. */
function PanneauTelephone({ actuel }: { actuel: string | null }) {
  const [valeur, setValeur] = useState(actuel ?? '');
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const notifier = useNotifier();
  const qc = useQueryClient();
  const enregistrer = async (e: FormEvent) => {
    e.preventDefault();
    setErreur(null); setEnvoi(true);
    try {
      const r = await api.put<{ telephonePro: string | null }>('/api/compte/telephone', { telephone: valeur.trim() || null });
      setValeur(r.telephonePro ?? '');
      notifier(r.telephonePro ? 'Téléphone professionnel enregistré.' : 'Téléphone professionnel retiré.');
      await qc.invalidateQueries({ queryKey: ['moi'] });
    } catch (err) { setErreur(messageErreur(err)); } finally { setEnvoi(false); }
  };
  return (
    <Panneau titre="Téléphone professionnel">
      <form className="pile" onSubmit={enregistrer} noValidate>
        <p className="petit secondaire">Vos collègues qui ont accès à un site le voient dans le carnet des équipements où vous êtes intervenu, avec un bouton « Appeler ». Il est masqué dès que votre compte est désactivé. N’indiquez jamais un numéro personnel.</p>
        {erreur && <Alerte ton="danger">{erreur}</Alerte>}
        <Champ libelle="Numéro professionnel" type="tel" inputMode="tel" autoComplete="tel" maxLength={40} value={valeur}
          onChange={(x) => setValeur(x.target.value)} placeholder="ex. 06 12 34 56 78" aide="Laissez vide pour ne pas en indiquer." />
        <div><Bouton type="submit" chargement={envoi} icone={<Phone aria-hidden />}>Enregistrer le téléphone</Bouton></div>
      </form>
    </Panneau>
  );
}

export function PageCompte() {
  const { moi, etat, peut } = useSession();
  // Déconnexion bloquée tant que des saisies attendent le réseau (incrément 6) : même règle que la barre de navigation.
  const { deconnecter, element: elementDeconnexion } = useDeconnexion();

  const horsLigne = useHorsLigne();
  const [theme, setTheme] = useTheme();
  const [vue, setVue] = useVue();
  return (
    <div className="pile-l" style={{ maxWidth: 860 }}>
      {elementDeconnexion}
      <EntetePage titre="Mon compte" sousTitre={`${moi.utilisateur.nom} — ${moi.utilisateur.email}`}
        actions={<>{peut('admin.utilisateurs') && <LienBouton to="/administration" icone={<Settings aria-hidden />}>Administration</LienBouton>}
          <Bouton icone={<LogOut aria-hidden />} onClick={deconnecter}>Se déconnecter</Bouton></>} />
      <PanneauSecurite mfa={!!moi.utilisateur.mfa} />
      <Panneau titre="Rôles et organisation">
        <dl className="fiche">
          <div><dt>Organisation</dt><dd>{moi.organisation.nom}</dd></div>
          <div><dt>Rôles</dt><dd>{moi.roles.map((r) => r.libelle).join(', ')}</dd></div>
          <div><dt>Assistance IA</dt><dd>{etat?.ia.disponible ? `Configurée (${etat.ia.fournisseur})` : 'Non configurée'}</dd></div>
        </dl>
      </Panneau>
      <Panneau titre="Affichage sur cet appareil">
        <div className="pile">
          <div className="pile" style={{ gap: 6 }}>
            <span className="champ-libelle" id="lib-vue">Densité</span>
            <div className="segment" role="group" aria-labelledby="lib-vue">
              <button type="button" aria-pressed={vue === 'terrain'} onClick={() => setVue('terrain')}>Terrain</button>
              <button type="button" aria-pressed={vue === 'expert'} onClick={() => setVue('expert')}>Expert</button>
            </div>
            <span className="champ-aide">Terrain : texte plus grand et l’essentiel seulement. Expert : toutes les colonnes. Les données sont les mêmes ; les avertissements restent visibles dans les deux vues.</span>
          </div>
          <div className="pile" style={{ gap: 6 }}>
            <span className="champ-libelle" id="lib-theme">Thème</span>
            <div className="segment" role="group" aria-labelledby="lib-theme">
              <button type="button" aria-pressed={theme === 'systeme'} onClick={() => setTheme('systeme')}>Système</button>
              <button type="button" aria-pressed={theme === 'light'} onClick={() => setTheme('light')}>Clair</button>
              <button type="button" aria-pressed={theme === 'dark'} onClick={() => setTheme('dark')}>Sombre</button>
            </div>
          </div>
        </div>
      </Panneau>
      <Panneau titre="Hors connexion sur cet appareil">
        {!horsLigne.possible ? <p className="petit discret">Le fonctionnement hors connexion n’est pas disponible dans ce navigateur (ou en mode développement).</p> : (
          <div className="pile">
            <p className="petit secondaire">Seuls les éléments que vous choisissez (bouton « Garder hors connexion » sur une fiche ou un document) sont conservés, {DUREE_JOURS} jours au plus, et tout est effacé à la déconnexion. Hors connexion, l’assistance IA et l’enregistrement sur le serveur sont indisponibles ; vos brouillons restent sur l’appareil jusqu’au retour du réseau.</p>
            {horsLigne.liste.length === 0 ? <p className="petit discret">Rien n’est conservé sur cet appareil.</p> : (
              <div className="liste">{horsLigne.liste.map((x) => (
                <div key={x.cle} className="liste-element"><span className="liste-element-corps"><span className="liste-element-titre">{x.libelle}</span>
                  <span className="petit discret">Gardé {depuis(new Date(x.le).toISOString())}</span></span>
                  <Bouton compact variante="fantome" onClick={() => retirer(x.cle)}>Retirer</Bouton></div>))}</div>
            )}
          </div>
        )}
      </Panneau>
      <PanneauTelephone actuel={moi.utilisateur.telephonePro ?? null} />
    </div>
  );
}

/** Écran imposé quand la double authentification est obligatoire et pas encore activée. */
export function EcranMfaImpose({ moi }: { moi: Moi }) {
  return (
    <div className="connexion-panneau" style={{ minHeight: '100dvh' }}>
      <div className="pile-l" style={{ width: '100%', maxWidth: 520 }}>
        <div className="pile" style={{ gap: 6 }}>
          <span className="marque-nom" style={{ fontSize: 'var(--t-xl)' }}>TRAME</span>
          <h1 style={{ fontSize: 'var(--t-xl)' }}>Sécurisez votre compte</h1>
          <p className="secondaire">Bonjour {moi.utilisateur.nom}. Votre organisation exige la double authentification avant d’accéder aux données.</p>
        </div>
        <ActivationMfa />
      </div>
    </div>
  );
}
